import { useEffect, useRef, useState } from 'react';

/**
 * Auto-size text to fill its container without overflowing.
 *
 * Binary-searches the largest font size where the element does not overflow its
 * container horizontally (`scrollWidth <= clientWidth`) nor, optionally, vertically.
 * Re-runs on text change and whenever the element is resized (via `ResizeObserver`).
 *
 * Attach the returned `ref` to the text element and apply `fontSize` (in px) to it.
 * All `*Em` bounds are resolved against the parent's font-size, which stays stable
 * while the element's own font-size is being searched over.
 *
 * @param {string} text - Text content; changing it re-triggers the fit.
 * @param {object} [options]
 * @param {number} [options.minEm=1] - Lower bound for the font size, in em.
 * @param {number} [options.maxEm] - Upper bound for the font size, in em.
 *   Defaults to the element's `clientWidth` (px), so it scales with container width.
 * @param {number} [options.maxHeightEm] - Max allowed element height, in em.
 *   When set, the font is also capped so `scrollHeight` stays within it
 *   (e.g. to limit wrapping to N lines). Unbounded when omitted.
 * @returns {{ ref: import('react').RefObject<any>, fontSize: number }}
 *   `ref` to attach to the text element and the fitted `fontSize` in px.
 */
const useFitText = (text, { minEm = 1, maxEm = null, maxHeightEm = null } = {}) => {
  const ref = useRef(null);
  const [fontSize, setFontSize] = useState(0);

  useEffect(() => {
    const element = ref.current;

    const fit = () => {
      if (!element) return;
      // em resolved against the parent's (stable) font-size, since the element's
      // own font-size is what we're searching over.
      const basePx = Number.parseFloat(getComputedStyle(element.parentElement).fontSize);
      const maxHeight = maxHeightEm == null ? Infinity : maxHeightEm * basePx;
      let best = minEm * basePx;
      let high = maxEm == null ? element.clientWidth : maxEm * basePx;
      let low = minEm * basePx;
      while (low <= high) {
        const mid = (low + high) >> 1;
        element.style.fontSize = `${mid}px`;
        if (element.scrollWidth <= element.clientWidth && element.scrollHeight <= maxHeight) {
          best = mid;
          low = mid + 1;
        } else {
          high = mid - 1;
        }
      }
      element.style.fontSize = '';
      setFontSize(best);
    };

    const observer = new ResizeObserver(fit);
    if (element) observer.observe(element);
    fit();
    return () => observer.disconnect();
  }, [text, minEm, maxEm, maxHeightEm]);

  return { ref, fontSize };
};
export default useFitText;
